import type { QueueRow, QueueState } from '../api/types';

/** The six BullMQ states a queue's drawer can show, in the order the tabs run. */
export const STATE_META: { key: QueueState; label: string; help: string; count: (row: QueueRow) => number }[] = [
  {
    key: 'waiting',
    label: 'Waiting',
    help: 'Queued without a priority and taken in order. Oldest first — the top row is what the next free worker picks up.',
    count: (row) => row.wait,
  },
  {
    key: 'prioritized',
    label: 'Prioritized',
    help: 'Queued with a priority; dispatch-be enqueues almost everything this way. Next to run first.',
    count: (row) => row.prioritized,
  },
  {
    key: 'active',
    label: 'Active',
    help: 'Being processed right now. Longest-running first — a job at the top for many minutes is stuck, not busy.',
    count: (row) => row.active,
  },
  {
    key: 'delayed',
    label: 'Delayed',
    help: 'Scheduled for later: retries waiting out their backoff, and every scheduler’s next occurrence. Soonest first.',
    count: (row) => row.delayed,
  },
  {
    key: 'failed',
    label: 'Failed',
    help: 'Out of attempts. Kept for seven days, so the count is a rolling window; the reason on each row is what matters.',
    count: (row) => row.failed,
  },
  {
    key: 'completed',
    label: 'Done',
    help: 'Finished and still kept (a day, or dropped on the spot on the location queues). Most recent first.',
    count: (row) => row.completed,
  },
];

const KNOWN_STATES = STATE_META.map((entry) => entry.key);
/** A state from a URL or a find result; anything unknown opens on Waiting. */
export const toState = (state: string): QueueState =>
  KNOWN_STATES.includes(state as QueueState) ? (state as QueueState) : 'waiting';

/** Bull Board’s own URL for a queue, opened on the same state. Linked, never framed. */
export const boardQueueUrl = (board: string | null | undefined, queue: string, state?: QueueState): string | null =>
  board ? `${board.replace(/\/$/, '')}/queue/${encodeURIComponent(queue)}${state ? `?status=${state}` : ''}` : null;
